import React from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import {
  FiX,
  FiZap,
  FiUsers,
  FiBarChart2,
  FiSearch,
  FiMail,
  FiMessageSquare,
  FiBell,
  FiPhone,
  FiFileText,
  FiEdit3,
  FiTrendingUp,
  FiHash,
  FiUser,
  FiCopy,
  FiMic
} from 'react-icons/fi';
import type {
  Workflow,
  WorkflowTouchpoint
} from '../../../services/workflowService';

// ── Helpers (mirrored from WorkflowsGrid.tsx) ─────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  running: '#10b981',
  draft: '#f59e0b',
  paused: '#3b82f6',
  stopped: '#6b7280'
};

const TOUCHPOINT_COLORS: Record<string, string> = {
  email: '#34875a',
  manual: '#006de0',
  sms: '#a0522d',
  call: '#7c3aed',
  other: '#d97706',
  notification: '#374151',
  nowcerts_task: '#006de0',
  opportunity_stage: '#e5a343',
  create_opportunity: '#34875a',
  add_tag: '#04bb99',
  remove_tag: '#bd3d00',
  insured_note: '#8b5cf6',
  webhook_trigger: '#0ea5e9',
  clone_policy_as_quote: '#6b7280',
  loss_run: '#f43f5e',
  agent_assignment: '#f59e0b',
  momentum_voice: '#FF6820'
};

const TOUCHPOINT_LABELS: Record<string, string> = {
  email: 'Automated Email',
  manual: 'Manual Email',
  sms: 'Automated SMS',
  call: 'Phone Call',
  other: 'Instructions',
  notification: 'Notification',
  nowcerts_task: 'Create Task',
  opportunity_stage: 'Change Stage',
  create_opportunity: 'Create Opportunity',
  add_tag: 'Add Tag',
  remove_tag: 'Remove Tag',
  insured_note: 'Note',
  webhook_trigger: 'Webhook',
  clone_policy_as_quote: 'Clone Policy',
  loss_run: 'Loss Run',
  agent_assignment: 'Agent / CSR Assignment',
  momentum_voice: 'Momentum Voice'
};

function getStatusColor(s: string): string {
  return STATUS_COLORS[s] ?? '#6b7280';
}

function getTouchpointColor(type: string): string {
  return TOUCHPOINT_COLORS[type] ?? '#6b7280';
}

function getTouchpointLabel(type: string): string {
  return TOUCHPOINT_LABELS[type] ?? type;
}

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + '…' : str;
}

function getTpDetail(tp: WorkflowTouchpoint): string | null {
  const attr = tp.emails_attributes?.[0];
  const custom = tp.custom_task_attributes;

  switch (tp.type) {
    case 'email':
    case 'manual':
    case 'notification':
      return attr?.subject ? truncate(attr.subject, 60) : null;
    case 'sms':
      return attr?.body ? truncate(attr.body, 60) : null;
    case 'call':
    case 'other':
      return tp.content ? truncate(tp.content, 60) : null;
    case 'nowcerts_task': {
      const parts = [
        custom?.category_name,
        custom?.descriptive_time,
        custom?.assigne_type
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'opportunity_stage':
      return custom?.stage ? `Stage: ${custom.stage}` : null;
    case 'create_opportunity': {
      const parts = [custom?.stage, custom?.assigne_type].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'insured_note': {
      const parts = [
        custom?.note_type,
        custom?.subject ? truncate(custom.subject, 50) : null
      ].filter(Boolean);
      return parts.length ? parts.join(' — ') : null;
    }
    case 'webhook_trigger':
      return custom?.webhook_url
        ? `${custom.webhook_action_type ?? 'POST'} ${truncate(custom.webhook_url, 40)}`
        : null;
    case 'clone_policy_as_quote': {
      const parts = [
        custom?.billing_type,
        custom?.copy_selected_items?.length
          ? custom.copy_selected_items.join(', ')
          : null
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'loss_run': {
      const parts = [
        custom?.nowcerts_carrier_mga?.insurance_company,
        custom?.email_template?.name,
        custom?.reminder_days != null
          ? `Remind after ${custom.reminder_days}d`
          : null
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'agent_assignment':
      return 'Assigns agents / CSRs';
    default:
      return null;
  }
}

function TouchpointIcon({ type, size = 13 }: { type: string; size?: number }) {
  const icons: Record<string, React.ReactElement> = {
    email: <FiMail size={size} />,
    manual: <FiEdit3 size={size} />,
    sms: <FiMessageSquare size={size} />,
    notification: <FiBell size={size} />,
    call: <FiPhone size={size} />,
    other: <FiFileText size={size} />,
    nowcerts_task: <FiFileText size={size} />,
    opportunity_stage: <FiTrendingUp size={size} />,
    create_opportunity: <FiTrendingUp size={size} />,
    add_tag: <FiHash size={size} />,
    remove_tag: <FiHash size={size} />,
    insured_note: <FiEdit3 size={size} />,
    webhook_trigger: <FiZap size={size} />,
    clone_policy_as_quote: <FiCopy size={size} />,
    loss_run: <FiBarChart2 size={size} />,
    agent_assignment: <FiUser size={size} />,
    momentum_voice: <FiMic size={size} />
  };
  return icons[type] ?? <FiZap size={size} />;
}

// ── Props & component ─────────────────────────────────────────────────────────

interface WorkflowDetailModalProps {
  workflow: Workflow | null;
  isMine: boolean;
  onClose: () => void;
  actionSlot?: React.ReactNode;
}

const WorkflowDetailModal: React.FC<WorkflowDetailModalProps> = ({
  workflow,
  isMine,
  onClose,
  actionSlot
}) => {
  if (!workflow) return null;
  const statusColor = getStatusColor(workflow.status);
  const tpCount = workflow.touchpoints.length;

  return ReactDOM.createPortal(
    <motion.div
      className='mws-modal-backdrop'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className='mws-modal'
        initial={{ opacity: 0, scale: 0.93, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 20 }}
        transition={{ duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='mws-modal__head'>
          <div
            className='mws-modal__icon'
            style={{ background: statusColor + '22', color: statusColor }}
          >
            <FiZap size={16} />
          </div>
          <div className='mws-modal__title'>{workflow.name}</div>
          <span
            className='mws-modal__badge'
            style={{
              background: statusColor + '1a',
              color: statusColor,
              border: `1px solid ${statusColor}44`
            }}
          >
            {workflow.status}
          </span>
          <button className='mws-modal__close' onClick={onClose}>
            <FiX size={12} />
          </button>
        </div>

        {/* Body */}
        <div className='mws-modal__body'>
          {/* Creator */}
          <div className='mws-modal__creator'>
            <img
              src={workflow.user?.avatar?.url}
              alt={workflow.user?.name ?? ''}
              className='mws-modal__creator-avatar'
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span>
              {workflow.user?.name}
              {workflow.updated_at &&
                ` · Updated ${new Date(workflow.updated_at).toLocaleDateString()}`}
            </span>
          </div>

          {/* Stats — My workflows only */}
          {isMine && (
            <div className='mws-modal__stats'>
              {[
                {
                  icon: <FiZap size={12} style={{ color: statusColor }} />,
                  value: tpCount,
                  label: 'Steps'
                },
                {
                  icon: <FiUsers size={12} style={{ color: '#22d3ee' }} />,
                  value: workflow.workflow_contacts_count,
                  label: 'Contacts'
                },
                {
                  icon: <FiSearch size={12} style={{ color: '#8b5cf6' }} />,
                  value: workflow.opened_count,
                  label: 'Opened'
                },
                {
                  icon: <FiBarChart2 size={12} style={{ color: '#f59e0b' }} />,
                  value: workflow.clicked_count,
                  label: 'Clicked'
                }
              ].map(({ icon, value, label }) => (
                <div key={label} className='mws-modal__stat'>
                  {icon}
                  <div className='mws-modal__stat-value'>{value}</div>
                  <div className='mws-modal__stat-label'>{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Trigger */}
          {workflow.workflow_trigger?.filter_condition && (
            <div>
              <div className='mws-modal__section-label'>Trigger</div>
              <div className='mws-modal__trigger-grid'>
                {workflow.workflow_trigger.filter_condition.trigger_category
                  ?.name && (
                  <div className='mws-modal__trigger-row'>
                    <span className='mws-modal__trigger-key'>Name</span>
                    <span className='mws-modal__trigger-val'>
                      {
                        workflow.workflow_trigger.filter_condition
                          .trigger_category.name
                      }
                    </span>
                  </div>
                )}
                {workflow.workflow_trigger.filter_condition.trigger_category
                  ?.category_type && (
                  <div className='mws-modal__trigger-row'>
                    <span className='mws-modal__trigger-key'>Type</span>
                    <span className='mws-modal__trigger-val'>
                      {
                        workflow.workflow_trigger.filter_condition
                          .trigger_category.category_type
                      }
                    </span>
                  </div>
                )}
                {workflow.workflow_trigger.filter_condition.name && (
                  <div className='mws-modal__trigger-row'>
                    <span className='mws-modal__trigger-key'>Condition</span>
                    <span className='mws-modal__trigger-val'>
                      {workflow.workflow_trigger.filter_condition.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Touchpoints timeline */}
          {tpCount > 0 && (
            <div>
              <div className='mws-modal__section-label'>
                Touchpoints ({tpCount})
              </div>
              <div className='mws-modal__tp-timeline'>
                {workflow.touchpoints.map((tp, i) => {
                  const isLast = i === tpCount - 1;
                  const nextTp = workflow.touchpoints[i + 1];
                  const dayGap = nextTp ? nextTp.delay - tp.delay : null;
                  const tpColor = getTouchpointColor(tp.type);
                  const tpDetail = getTpDetail(tp);

                  return (
                    <div
                      key={tp.id}
                      className={`mws-modal__tp-step${isLast ? ' mws-modal__tp-step--last' : ''}`}
                    >
                      <div className='mws-modal__tp-track'>
                        <div
                          className='mws-modal__tp-dot'
                          style={{
                            background: tpColor + '20',
                            color: tpColor,
                            border: `1.5px solid ${tpColor}55`
                          }}
                        >
                          {i + 1}
                        </div>
                        {!isLast && (
                          <div
                            className='mws-modal__tp-connector'
                            style={{ background: tpColor + '30' }}
                          />
                        )}
                      </div>
                      <div className='mws-modal__tp-card'>
                        <div
                          className='mws-modal__tp-card-icon'
                          style={{ background: tpColor + '18', color: tpColor }}
                        >
                          <TouchpointIcon type={tp.type} />
                        </div>
                        <div className='mws-modal__tp-card-body'>
                          <span className='mws-modal__tp-card-type'>
                            {getTouchpointLabel(tp.type)}
                          </span>
                          {tpDetail && (
                            <span className='mws-modal__tp-card-detail'>
                              {tpDetail}
                            </span>
                          )}
                          {dayGap !== null && dayGap > 0 && (
                            <span className='mws-modal__tp-card-gap'>
                              +{dayGap} day{dayGap !== 1 ? 's' : ''} until next
                              step
                            </span>
                          )}
                        </div>
                        <div className='mws-modal__tp-card-day'>
                          Day {tp.delay}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {actionSlot && <div className='mws-modal__footer'>{actionSlot}</div>}
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default WorkflowDetailModal;
